import { useAuth } from "@/src/contexts/auth.context";
import { InvitableMember } from "@/src/models/user.model";
import {
	useCreateStarTransaction,
	useGetCurrentActiveSeason,
	useGetStarTransactionsInfinite,
	useGetUserStarBalance,
	useUpdateUserStarBalance,
} from "@/src/services/api/rewardly/rewardly.hooks";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";

/** Reference design formats rewards as e.g. "$75.00". */
export const formatReward = (stars: number, monetaryValue?: number) =>
	`$${(stars * (monetaryValue ?? 1)).toFixed(2)}`;

export const useMyStars = () => {
	const { t } = useTranslation();
	const { user, orgId } = useAuth();
	const { mutateAsync: createStarTransaction, isPending: isSending } = useCreateStarTransaction();
	const { mutateAsync: updateBalance } = useUpdateUserStarBalance();
	const { data: seasonData, isLoading: isSeasonLoading } = useGetCurrentActiveSeason(orgId ?? "");
	const currentSeason = seasonData?.getseasons?.items?.[0];

	const { data: transactionsData, isLoading: isTransactionsLoading, refetch } =
		useGetStarTransactionsInfinite(currentSeason?.ItemId ?? "");

	const { data: seasonBalance } = useGetUserStarBalance(user?.itemId ?? "", currentSeason?.ItemId ?? "")
	const seasonAllowance = seasonBalance?.getuserStarBalances?.items?.[0]?.starsRemaining ?? 0;

	const starsReceived = useMemo(() => {
		const allTransactions = transactionsData?.pages.flatMap(
			page => page?.getstarTransactions?.items ?? []
		) ?? [];
		return allTransactions
			.filter(t => t.recipientId === user?.itemId)
			.reduce((sum, t) => sum + (t.amount ?? 0), 0);
	}, [transactionsData, user?.itemId]);

	const rewardValue = formatReward(starsReceived, currentSeason?.monetaryValue);

	const isLoading = isSeasonLoading || isTransactionsLoading;
	const [star, setStars] = useState("");
	const [reason, setReason] = useState("");
	const [usersModalVisible, setUsersModalVisible] = useState(false);
	const [selectedUsers, setSelectedUsers] = useState<InvitableMember[]>([]);

	const [showStarBurstAnimation, setShowStarBurstAnimation] = useState(false);

	const removeUser = (user: InvitableMember) =>
		setSelectedUsers(prev => prev.filter(u => u.itemId !== user.itemId));

	const validateSend = (): string | null => {
		if (selectedUsers.length === 0) return t("ERROR_SELECT_RECIPIENT");
		const amount = Number(star);
		if (!star || isNaN(amount) || amount <= 0) return t("ERROR_INVALID_STAR_AMOUNT");
		const min = currentSeason?.minGiveAmt;
		const max = currentSeason?.maxGiveAmt;
		if ((min != null && amount < min) || (max != null && amount > max)) {
			return t("ERROR_STARS_RANGE", { min: min ?? 1, max: max ?? amount });
		}
		if (!reason.trim()) return t("ERROR_REASON_REQUIRED");
		if (amount * selectedUsers.length > seasonAllowance) {
			return t("ERROR_INSUFFICIENT_ALLOWANCE");
		}
		return null;
	};

	const handleSendStars = async () => {
		if (!currentSeason?.ItemId || !user?.itemId) return;

		const validationError = validateSend();
		if (validationError) {
			Toast.show({ type: "error", text1: validationError });
			return;
		}

		const totalStarsToSend = Number(star) * selectedUsers.length;

		try {
			const transactions = selectedUsers.map(recipient =>
				createStarTransaction({
					seasonId: currentSeason.ItemId,
					senderId: user.itemId,
					recipientId: recipient.itemId,
					senderInfo: {
						userId: user.itemId,
						email: user.email,
						displayName: `${user.firstName} ${user.lastName}`,
						profileImageUrl: user.profileImageUrl as string,
					},
					recipientInfo: {
						userId: recipient.itemId,
						email: recipient.email,
						displayName: `${recipient.firstName} ${recipient.lastName}`,
						profileImageUrl: recipient.profileImageUrl as string,
					},
					amount: Number(star),
					message: reason || undefined,
					type: "recognition",
				})
			);

			const results = await Promise.allSettled(transactions);
			const failures = results.filter(r => r.status === "rejected");

			if (failures.length > 0) {
				Toast.show({ type: "error", text1: t("ERROR_STARS_SEND_FAILED") });
				return;
			}

			await updateBalance({
				userId: user.itemId,
				seasonId: currentSeason.ItemId,
				starsRemaining: seasonAllowance - totalStarsToSend,
			});

			setStars("");
			setReason("");
			setSelectedUsers([]);
			setUsersModalVisible(false);
			setShowStarBurstAnimation(true);
			Toast.show({ type: "success", text1: t("MSG_STARS_SENT_SUCCESS") });
			refetch(); // refresh transactions so derived values update
		} catch {
			Toast.show({ type: "error", text1: t("ERROR_STARS_SEND_FAILED") });
		}
	};

	return {
		currentSeason,
		seasonAllowance,
		starsReceived,
		rewardValue,
		isLoading,
		isSending,
		star, setStars,
		reason, setReason,
		selectedUsers, setSelectedUsers,
		usersModalVisible, setUsersModalVisible,
		removeUser,
		handleSendStars,
		showStarBurstAnimation,
		setShowStarBurstAnimation
	};
};
